import { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2, Shield } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// Handles the one-time #session_id fragment Emergent Google auth appends to the
// redirect URL: exchanges it server-side for our own tfd_session cookie.
export default function GoogleAuthCallback() {
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { login } = useAuth();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || '';
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;
    const landingPath = window.location.pathname || '/account';
    // Scrub the fragment immediately so the one-time session_id never lingers
    // in the address bar or browser history.
    window.history.replaceState(null, '', landingPath + window.location.search);

    if (!sessionId) {
      navigate('/account/sign-in', { replace: true });
      return;
    }

    (async () => {
      try {
        const res = await api.post('/auth/google/session', { session_id: sessionId });
        const { access_token, user } = res.data;
        await login(access_token, user);
        const isStaff = user.account_type === 'staff' || user.role === 'admin';
        const dest = isStaff ? '/team' : (landingPath && landingPath !== '/' ? landingPath : '/account');
        navigate(dest, { replace: true, state: { user } });
      } catch (err) {
        setError(err.response?.data?.detail || 'Google sign-in failed. Please try again.');
      }
    })();
  }, [navigate, login]);

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'hsl(var(--bg-paper))' }}>
      <div className="w-full max-w-sm px-4 text-center" data-testid="google-auth-callback">
        <div className="inline-flex items-center gap-2 mb-6">
          <Shield className="h-6 w-6 text-ink" strokeWidth={1.5} />
          <span className="font-display text-lg font-medium text-ink">The Fashion Detective</span>
        </div>
        {!error ? (
          <div className="flex flex-col items-center gap-3" data-testid="google-auth-loading">
            <Loader2 className="h-6 w-6 animate-spin text-ink" />
            <p className="text-sm text-muted-foreground">Completing your sign-in…</p>
          </div>
        ) : (
          <div className="space-y-4">
            <Alert variant="destructive">
              <AlertDescription className="text-xs" data-testid="google-auth-error">{error}</AlertDescription>
            </Alert>
            <Button asChild className="rounded-[12px] h-11" data-testid="google-auth-retry-btn">
              <Link to="/account/sign-in">Back to Sign In</Link>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
